import { useDispatch, useSelector } from 'react-redux';
import { sounds } from '../assets';
import { actions } from '../redux';
import { playSound, getPieceAtSquare, dbg, dbgBoard } from '../utils';
import { useIsWhite } from '../hooks';
import { GAME_STATES } from '../constants';
import { useRef, useEffect } from 'react';

export const useBoardSocketHandlers = ({
    setRoomMessage,
    setGameOverReason,
    setGameOverResult,
    setmyEloChange,
    setOpponentEloChange,
    setDisplayWinLossPopup,
    setDisconnectCountdown,
    setDrawOfferPending,
    setDrawOfferDeclinedMsg,
    setRematchOffered,
    setRematchOfferedBy,
    setRematchRequested,
    onRematchDeclined,     // called when opponent declines; replaces string-matched setRoomMessage
    onRematchReady,
    onExplosion,
    onDetonation,
}) => {

    const dispatch = useDispatch();                          // sends actions to redux store

    // useRef needed because the is white is updated after handlers connected,
    // so we need updated reference to isWhite
    const isWhite = useIsWhite();
    const isWhiteRef = useRef(isWhite);
    useEffect(() => {
        isWhiteRef.current = isWhite;
    }, [isWhite]);

    // Track move history length so we know which history index an explosion falls on
    const moveHistory = useSelector(state => state.game.moveHistory);
    const moveHistoryLengthRef = useRef(moveHistory.length);
    useEffect(() => {
        moveHistoryLengthRef.current = moveHistory.length;
    }, [moveHistory]);

    // Track bomb state for debug logging
    const playerBombs = useSelector(state => state.game.player.bombs);
    const playerBombsRef = useRef(playerBombs);
    useEffect(() => { playerBombsRef.current = playerBombs; }, [playerBombs]);
    const oppBombs = useSelector(state => state.game.opponent.bombs);
    const oppBombsRef = useRef(oppBombs);
    useEffect(() => { oppBombsRef.current = oppBombs; }, [oppBombs]);

    // Track player rating so game-over handlers can update it with the ELO delta
    const playerRating = useSelector(state => state.game.player.rating);
    const playerRatingRef = useRef(playerRating);
    playerRatingRef.current = playerRating;

    // Track whether the most recent explosion was a king (affects win/loss popup delay)
    const lastExplosionWasKingRef = useRef(false);

    const handleRoomCreated = ({ message }) => {
        setRoomMessage(message);
        dispatch(actions.setGameState(GAME_STATES.matching));
    };

    const handleRoomJoinError = ({ message }) => {
        setRoomMessage(message);
        dispatch(actions.setGameState(GAME_STATES.inactive));
    };

    const handleDisconnect = ({ timeoutMs, message }) => {
        setRoomMessage(message);
        setDisconnectCountdown(Math.floor(timeoutMs / 1000));
    };

    const handlePlayerRejoined = () => {
        setDisconnectCountdown(null);
        setRoomMessage("");
    };

    const handleStartPlay = ({ myBombs, oppBombCount, whitePlayerBombs, blackPlayerBombs }) => {
        dbg('startPlay', `Game starting. I am ${isWhiteRef.current ? 'WHITE' : 'BLACK'}`);
        dbg('startPlay', `My bombs locked in: ${playerBombsRef.current.join(', ') || '—'}`);
        dbg('startPlay', `Opponent bomb count: ${oppBombCount ?? oppBombsRef.current.length}`);

        if (myBombs) {
            // Per-socket shape: server confirms our bomb list (bomb positions already set via bombPlaced).
            dispatch(actions.setMyBombs(myBombs));
        } else if (whitePlayerBombs != null && blackPlayerBombs != null) {
            // Legacy broadcast shape from timeout: randomized bombs. Extract only our own.
            const ownBombs = isWhiteRef.current ? whitePlayerBombs : blackPlayerBombs;
            setRoomMessage("Randomly placed bombs on timeout!");
            playSound(sounds.shovel);
            dispatch(actions.setMyBombs(ownBombs));
        }

        playSound(sounds.gameStart);

        // double check every highlighted square is removed
        document.querySelectorAll('div.highlighted').forEach(div => div.remove());

        dispatch(actions.setGameState(GAME_STATES.playing));
    };

    const handleGameState = ({ gameFen, moveSan, specialMove, sideToMoveNext, preExplosionFen }) => {
        // determine who just made this move
        const isNextMoveWhite = !(sideToMoveNext === "b");
        const wasMyMove = isWhiteRef.current !== isNextMoveWhite;

        const moveNumber = Math.ceil((moveHistoryLengthRef.current + 1) / 2);
        dbg('gameState', `Move #${moveNumber} by ${wasMyMove ? 'ME' : 'OPPONENT'}: ${moveSan}${specialMove ? ` [${specialMove}]` : ''}`);
        dbg('gameState', `FEN: ${gameFen}`);
        dbgBoard(gameFen, {
            myBombs: playerBombsRef.current,
            craters: [],   // craters tracked in board-page; will be logged there on explosion
            oppBombCount: oppBombsRef.current.length,
        });

        // update the game normally when move isn't an explosion
        // note: explosions have custom timing / updates
        if (!specialMove || !specialMove.startsWith("explode ")) {
            dispatch(actions.updateGameFromServer(gameFen, moveSan));
        }

        // see what sort of sound we need to play based on the move just made
        if (specialMove) {
            if (specialMove.startsWith("explode ")) {
                const squareToExplode = specialMove.split(" ")[1];

                // Dispatch real gameFen immediately — Redux is always authoritative
                dispatch(actions.updateGameFromServer(gameFen, moveSan));
                dispatch(actions.detonateBomb(squareToExplode));

                const explosionMoveCount = moveHistoryLengthRef.current;
                playSound(sounds.ohNoBoom);

                // Identify piece from preExplosionFen (preExplosionFen is not dispatched to Redux)
                const detonatedPiece = getPieceAtSquare(preExplosionFen, squareToExplode);
                const isKing = detonatedPiece?.toLowerCase() === 'k';
                lastExplosionWasKingRef.current = isKing;

                // Show cinematic overlay immediately while board shows correct (post-explosion) position
                onDetonation(detonatedPiece);

                // Record crater after delay (overlay still visible at this point)
                setTimeout(() => {
                    onExplosion(squareToExplode, explosionMoveCount);
                }, 900);
            } else {
                switch (specialMove) {
                    case "capture":
                        playSound(sounds.capture);
                        break;
                    case "castle":
                        playSound(sounds.castle);
                        break;
                    case "promotion":
                        playSound(sounds.promote);
                        break;
                    case "checkmate":
                    case "stalemate":
                    case "draw":
                    case "draw by 50-move rule":
                    case "threefold repetition":
                    case "insufficient material":
                        playSound(sounds.gameEnd);
                        break;
                    case "in check":
                        playSound(sounds.moveCheck);
                        break;
                    default:
                        playSound(wasMyMove ? sounds.moveSelf : sounds.moveOpponent);
                        break;
                }
            }
        } else {
            // Play default move sound for regular moves
            playSound(wasMyMove ? sounds.moveSelf : sounds.moveOpponent);
        }

    };

    const handleinvalidMove = () => {
        playSound(sounds.illegal);
    };

    const handleDrawGameOver = ({ by, whiteEloChange, blackEloChange, whiteBombs, blackBombs }) => {
        setDrawOfferPending(false);
        setGameOverResult("Draw");
        setGameOverReason(by);
        const myDrawDelta = isWhiteRef.current ? whiteEloChange : blackEloChange;
        setmyEloChange(myDrawDelta);
        setOpponentEloChange(isWhiteRef.current ? blackEloChange : whiteEloChange);
        dispatch(actions.updatePlayerRating(playerRatingRef.current + myDrawDelta));
        // Reveal remaining opponent bombs now that the game is over
        const opponentBombs = isWhiteRef.current ? (blackBombs ?? []) : (whiteBombs ?? []);
        if (opponentBombs.length > 0) dispatch(actions.updateOpponentBombs(opponentBombs));
        
        // Delay the popup so the detonation overlay finishes before it appears.
        // Use the same timing as handleWinLossGameOver so king/non-king delays stay in sync.
        if (by.includes("explode")) {
            const delay = lastExplosionWasKingRef.current ? 5500 : 3200;
            setTimeout(() => setDisplayWinLossPopup(true), delay);
            playSound(sounds.gameEnd);
        } else {
            setTimeout(() => setDisplayWinLossPopup(true), 800);
            playSound(sounds.gameEnd);
        };

        dispatch(actions.setGameState(GAME_STATES.game_over));
    };

    const handleWinLossGameOver = ({ winner, by, whiteEloChange, blackEloChange, whiteBombs, blackBombs }) => {
        setDrawOfferPending(false);
        const isWinner = ((winner === 'w') && isWhiteRef.current) || ((winner === 'b') && !isWhiteRef.current);
        setGameOverResult(isWinner ? "You win" : "You lose");
        setGameOverReason(by);
        const myWinLossDelta = isWhiteRef.current ? whiteEloChange : blackEloChange;
        setmyEloChange(myWinLossDelta);
        setOpponentEloChange(isWhiteRef.current ? blackEloChange : whiteEloChange);
        dispatch(actions.updatePlayerRating(playerRatingRef.current + myWinLossDelta));
        // Reveal remaining opponent bombs now that the game is over
        const opponentBombs = isWhiteRef.current ? (blackBombs ?? []) : (whiteBombs ?? []);
        if (opponentBombs.length > 0) dispatch(actions.updateOpponentBombs(opponentBombs));

        // King detonation: wait for the cinematic before showing game over.
        // Regular explosions that end games use a shorter delay.
        if (by.includes("explode")) {
            const delay = lastExplosionWasKingRef.current ? 5500 : 3200;
            setTimeout(() => setDisplayWinLossPopup(true), delay);
            playSound(sounds.gameEnd);
        } else {
            setTimeout(() => setDisplayWinLossPopup(true), 800);
            playSound(sounds.gameEnd);
        };

        dispatch(actions.setGameState(GAME_STATES.game_over));
    };

    const handleSyncTime = ({ whiteTimeLeft, blackTimeLeft }) => {
        dispatch(actions.setTimers({ whiteTimeLeft, blackTimeLeft }));
    };

    const handleDrawOffer = () => {
        setDrawOfferPending(true);
    };

    const handleDrawOfferDeclined = () => {
        setDrawOfferDeclinedMsg('They declined. Cowards.');
    };

    const handleRematchOffered = ({ from }) => {
        setRematchOfferedBy(from);
        setRematchOffered(true);
    };

    const handleRematchDeclined = () => {
        setRematchRequested(false);
        onRematchDeclined();
    };

    const handleRematchReady = (gameData) => onRematchReady(gameData);

    return {
        handleRoomCreated,
        handleRoomJoinError,
        handleDisconnect,
        handleStartPlay,
        handleGameState,
        handleinvalidMove,
        handleDrawGameOver,
        handleWinLossGameOver,
        handleSyncTime,
        handlePlayerRejoined,
        handleDrawOffer,
        handleDrawOfferDeclined,
        handleRematchOffered,
        handleRematchDeclined,   // NEW
        handleRematchReady,
    };
};

